import { useCallback, useEffect, useRef, useState } from "react";
import { runHandler, type ResumeResult } from "../../../integrations/tools";
import { logConnectorFailure } from "../../telemetry";
import type { RefreshTokenFn } from "../../shared/api/fetchWithRefresh";
import { listFindings, patchFindingAnchor, patchFindingStatus } from "../services/api";
import { findingLocation, isLocatable } from "../location";
import type { Finding, FindingStatus } from "../types";

// Fix handlers whose write has a specific value worth re-checking after the
// fact (AIVA-7506 US12). Everything else is comment-only (broken_reference/
// defined_term_section_mismatch/defined_not_used/numbering_order) — a
// successful insertComment already IS the confirmation, so there is nothing
// further a second read would tell us.
const VERIFIABLE_WRITES: Record<string, "text" | "underline"> = {
  "word.docreview.fix_bracket_heading_mismatch": "text",
  "word.docreview.fix_inconsistent_formatting": "underline",
};

/**
 * Re-read the anchor's live state, independent of the write that just ran,
 * and confirm the specific value the fix intended actually landed. Falls
 * back to "applied" (not "verified") rather than throwing — a failed
 * re-check is not itself a failure, it just means the operator sees the
 * more honest of the two truthful statuses.
 */
async function verifyAppliedFix(finding: Finding): Promise<FindingStatus> {
  const field = VERIFIABLE_WRITES[finding.fix_handler];
  if (!field) return "verified";
  if (!finding.anchor_tag) return "applied";

  const result = await runHandler("word", "word.docreview.read_anchor_state", { anchorTag: finding.anchor_tag }, 10000);
  const state = result.ok
    ? (result.data as { state?: { text: string; underline: string } | null } | undefined)?.state
    : null;
  if (!state) return "applied";

  const expected =
    field === "text"
      ? (finding.fix_params as { correctFullText?: string }).correctFullText
      : (finding.fix_params as { targetUnderline?: string }).targetUnderline;
  return expected && state[field] === expected ? "verified" : "applied";
}

/**
 * Put the operator's cursor on what a fix or comment just wrote (AIVA-8875),
 * as the eye button would — applying one left the cursor wherever it was, so
 * the change had to be found by hand. The anchor outlives the fix, so it
 * lands on the changed text. Silent when nothing can be selected: the fix
 * itself succeeded, and "couldn't find this item" would read as if it had not.
 */
async function selectAppliedFix(finding: Finding): Promise<void> {
  if (!isLocatable(finding)) return;
  try {
    await runHandler(
      "word",
      "word.docreview.select_anchor",
      { anchorTag: finding.anchor_tag, ...findingLocation(finding) },
      10000,
    );
  } catch { /* nothing to report — the fix has already landed */ }
}

/** What a fix handler reports back. `applied` is the only success; the rest
 *  say why nothing was written. */
interface FixOutcome {
  applied?: boolean;
  stale?: boolean;
  reason?: string;
}

/**
 * Whether a fix declined purely because it had no usable location to write
 * to — as opposed to a real judgement about the content. Only two reasons
 * qualify (AIVA-8209):
 *
 * - `missing_args` — `anchorTag`/`expected` arrived null, i.e. this finding
 *   never anchored at all (the anchor step timed out, or its whole-word-only
 *   search couldn't match a `matchText` carrying punctuation). It carries no
 *   `stale` flag, so this case used to fall through to "unrecognised
 *   result" and leave the finding sitting there with Apply enabled and
 *   permanently useless.
 * - `anchor_missing` — it anchored once and the content control has since
 *   gone: a re-scan's `cleanupAllAnchors()`, "Done reviewing", or an edit
 *   over the anchored range.
 *
 * Every other reason — `text_changed` above all — is a genuine staleness
 * finding about the document and must never be repaired away.
 */
function needsAnchorRepair(data: FixOutcome | undefined): boolean {
  return data?.applied !== true && (data?.reason === "missing_args" || data?.reason === "anchor_missing");
}

function runFix(finding: Finding, anchorTag: string | null, expected: string | null): Promise<ResumeResult> {
  return runHandler("word", finding.fix_handler, { ...finding.fix_params, anchorTag, expected }, 15000);
}

/**
 * Run a finding's fix, and if it comes back saying it had nowhere to write
 * (see `needsAnchorRepair`), re-anchor from the finding's own text and run
 * it exactly once more (AIVA-8209 Layer 2).
 *
 * Retrying is safe because both repairable outcomes are refusals: the fix
 * bails before touching the document, so nothing was written the first time.
 * Exactly one retry — a second failure is a real failure, and a loop here
 * would just rewrite the same document repeatedly.
 *
 * Returns the finding as the fix actually saw it alongside the result, so
 * the caller's post-write verification re-reads the anchor that was really
 * used rather than the dead one still recorded in Cosmos.
 */
async function applyWithAnchorRepair(
  finding: Finding,
  apiToken: string | null,
  refresh?: RefreshTokenFn,
): Promise<{ result: ResumeResult; effective: Finding }> {
  const result = await runFix(finding, finding.anchor_tag, finding.expected);
  if (!result.ok || !needsAnchorRepair(result.data as FixOutcome | undefined)) {
    return { result, effective: finding };
  }

  const { matchText, occurrenceIndex } = findingLocation(finding);
  if (!matchText) return { result, effective: finding };

  const repair = await runHandler(
    "word",
    "word.docreview.repair_anchor",
    { matchText, occurrenceIndex: occurrenceIndex ?? 0 },
    15000,
  );
  const repaired = repair.ok ? (repair.data as { anchorTag?: string | null; fingerprint?: string | null }) : undefined;
  if (!repaired?.anchorTag) {
    console.warn(
      `[docReview] apply: could not repair the anchor for ${finding.rule_id} (finding ${finding.finding_id}) — keeping the original outcome`,
    );
    return { result, effective: finding };
  }

  // Never re-baseline a fingerprint the finding already has. The repair
  // reads whatever the document says NOW, so adopting it would leave the
  // fix comparing that text against itself — the staleness guard would pass
  // unconditionally and the fix would write over text the operator never
  // reviewed. A finding that never anchored has no fingerprint to protect,
  // so it takes the fresh one and gets a real guard for the first time.
  const expected = finding.expected ?? repaired.fingerprint ?? null;

  // Only a `pending` finding needs its repaired anchor recorded, and it needs
  // it BEFORE the fix runs: the server refuses to mark a pending finding
  // applied/verified, so without this the fix writes to the document and the
  // outcome then 409s — a real edit with no record of it, and Apply still
  // live to repeat that edit.
  //
  // An `anchored` finding whose content control was destroyed does NOT go
  // through this. Its outcome is already recordable (anchored -> applied/
  // verified is allowed), and the server refuses to re-anchor it on purpose:
  // from there, a dead anchor and a live one are indistinguishable, so
  // accepting the write would let a buggy client overwrite a good anchor.
  // The repaired tag is still used for this apply, just not persisted.
  const persisted =
    finding.status === "pending"
      ? await patchFindingAnchor(finding.thread_id, finding.finding_id, repaired.anchorTag, expected, apiToken, refresh)
      : { ...finding, anchor_tag: repaired.anchorTag, expected };

  return { result: await runFix(persisted, repaired.anchorTag, expected), effective: persisted };
}

/** What one Apply came to: `applied` wrote to the document, `stale` was
 *  refused because the document changed, `failed` hit an error and recorded
 *  nothing. */
export type ApplyOutcome = "applied" | "stale" | "failed";

export interface UseDocReviewFindings {
  findings: Finding[];
  loading: boolean;
  error: string | null;
  /** The finding currently being applied or skipped, or null. */
  actingId: string | null;
  refresh: () => void;
  /** Empty the local list without touching the server (AIVA-8180). Call
   *  after `word.docreview.cleanup_anchors` succeeds: that handler only
   *  removes the document's own hidden content controls, it never deletes
   *  the underlying Cosmos records, so a `refresh()` here would just
   *  re-fetch the SAME now-anchor-less findings — jumpToFinding/applyFinding
   *  would then silently fail against anchors that no longer exist. A local
   *  clear is the honest reflection of "the operator is done with this
   *  batch," independent of whatever the server-side records still say. */
  clearFindings: () => void;
  applyFinding: (finding: Finding) => Promise<ApplyOutcome>;
  skipFinding: (finding: Finding) => Promise<void>;
  /** Scroll to and select a finding's location in the document. Read-only —
   *  never gates on `actingId`/`busy`, since it writes nothing and can run
   *  freely alongside (or instead of) an apply/skip in flight. */
  jumpToFinding: (finding: Finding) => Promise<void>;
}

/**
 * Loads and lets the operator act on one document-review thread's findings
 * (AIVA-7506). Mirrors `useActivities`'s shape, but scoped by `threadId`
 * rather than polling globally — a finding list belongs to the conversation
 * that produced it, not the whole mailbox/account.
 *
 * Applying a finding never goes through the chat/LLM loop: the fix handler
 * already carries everything it needs (`fix_params`, `anchor_tag`,
 * `expected`), so it runs directly via the same `runHandler` dispatcher the
 * chat resume loop uses, and only the outcome is persisted back to Cosmos.
 */
export function useDocReviewFindings(
  threadId: string | null,
  apiToken: string | null,
  refreshTokens?: RefreshTokenFn,
): UseDocReviewFindings {
  const [findings, setFindings] = useState<Finding[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);

  const tokenRef = useRef(apiToken);
  tokenRef.current = apiToken;
  const refreshRef = useRef(refreshTokens);
  refreshRef.current = refreshTokens;

  const load = useCallback(async () => {
    if (!threadId) {
      setFindings([]);
      return;
    }
    setLoading(true);
    try {
      setError(null);
      const fetched = await listFindings(threadId, tokenRef.current, refreshRef.current);
      setFindings(fetched);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load findings");
      logConnectorFailure("word-doc-review", e, "load-findings");
    } finally {
      setLoading(false);
    }
  }, [threadId]);

  // load() swallows its own errors, so the floating promise never rejects.
  const refresh = useCallback(() => {
    load().catch(() => {});
  }, [load]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const clearFindings = useCallback(() => {
    setFindings([]);
    setError(null);
  }, []);

  const applyFinding = useCallback(async (finding: Finding): Promise<ApplyOutcome> => {
    setActingId(finding.id);
    try {
      setError(null);
      // AIVA-8209 Layer 2: the fix may report that it has no usable
      // location, in which case this re-anchors from the finding's own text
      // and runs it once more. `effective` is the finding as the fix
      // actually saw it — it carries the repaired anchor when that happened,
      // so the post-write re-read below checks the anchor really written.
      const { result, effective } = await applyWithAnchorRepair(finding, tokenRef.current, refreshRef.current);
      // A genuine failure (the handler threw, or runHandler's own 15s race
      // timed out) is NOT the same thing as a stale anchor — only a
      // successful call whose data explicitly says `stale: true` (the fix
      // handler's own honest "the document moved since this was found, I
      // wrote nothing" signal) may map to that status. Conflating the two
      // used to record every transient failure as "stale" too, which reads
      // to the operator as "document changed" and disables Apply
      // permanently — for a fixable network hiccup, not a real staleness.
      // An error here writes NO status at all: the finding stays exactly as
      // it was, so the operator can just retry.
      if (!result.ok) {
        setError(result.error ?? "Failed to apply the fix");
        return "failed";
      }
      const data = result.data as
        | { applied?: boolean; stale?: boolean; reason?: string; commentId?: string }
        | undefined;
      let status: FindingStatus;
      if (data?.applied === true) {
        status = await verifyAppliedFix(effective);
      } else if (data?.stale === true) {
        // The fix handler's own `reason` ("anchor_missing" vs "text_changed"
        // vs "already_sorted", etc.) never reached the operator UI or
        // telemetry before — only this generic "stale" status did, making
        // "document changed" and "this finding was never anchored at all"
        // indistinguishable from the outside. Logged to the console (not
        // just telemetry) specifically so it's visible via DevTools without
        // needing backend/telemetry access — see this rule's own recent
        // debugging session for why that mattered.
        console.warn(
          `[docReview] apply staled for ${finding.rule_id} (${finding.fix_handler}, finding ${finding.finding_id}): reason=${data.reason ?? "(none given)"}`,
        );
        status = "stale";
      } else {
        // The handler returned ok but neither a success nor a recognised
        // stale signal — an unexpected shape, not a real outcome to record.
        // Dump the raw payload to the console — this is exactly what a
        // `missing_args` guard (a null anchorTag/expected that slipped past
        // an `== null` check upstream, for instance) looks like from here,
        // and the generic UI message alone can't distinguish it from any
        // other unexpected shape.
        console.warn(
          `[docReview] apply returned an unrecognised result for ${finding.rule_id} (${finding.fix_handler}, finding ${finding.finding_id}):`,
          result.data,
        );
        setError("The fix handler returned an unexpected result.");
        return "failed";
      }
      const updated = await patchFindingStatus(
        finding.thread_id,
        finding.finding_id,
        status,
        tokenRef.current,
        refreshRef.current,
        // AIVA-8280: only a comment fix reports one. Recording it is what
        // lets the next scan tell this resolved finding from one the
        // operator has since undone, without comparing comment text that
        // several findings of the same rule routinely share.
        data.commentId,
      );
      setFindings((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
      if (status === "stale") return "stale";
      await selectAppliedFix(effective);
      return "applied";
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to apply the fix");
      logConnectorFailure("word-doc-review", e, "apply-finding");
      return "failed";
    } finally {
      setActingId(null);
    }
  }, []);

  const skipFinding = useCallback(async (finding: Finding) => {
    setActingId(finding.id);
    try {
      setError(null);
      const updated = await patchFindingStatus(
        finding.thread_id,
        finding.finding_id,
        "skipped",
        tokenRef.current,
        refreshRef.current,
      );
      setFindings((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to skip the finding");
      logConnectorFailure("word-doc-review", e, "skip-finding");
    } finally {
      setActingId(null);
    }
  }, []);

  const jumpToFinding = useCallback(
    async (finding: Finding) => {
      // AIVA-8209: a missing anchor_tag is no longer the end of the road —
      // the finding's own matchText/occurrenceIndex re-locate it. Only a
      // finding with neither is unreachable, and `isLocatable` keeps this
      // guard and the card's disabled state reading from one rule.
      if (!isLocatable(finding)) return;
      try {
        setError(null);
        const result = await runHandler(
          "word",
          "word.docreview.select_anchor",
          { anchorTag: finding.anchor_tag, ...findingLocation(finding) },
          10000,
        );
        // A failed select never throws — selectFindingLocation returns
        // `{selected: false}` when neither the anchor nor the text resolves
        // (e.g. a content control the operator deleted by hand plus text
        // that has since been rewritten), so `result.ok` is true here even
        // though nothing happened. Silently doing nothing left the operator
        // with no idea whether the click registered at all — this is the
        // "sometimes it just doesn't go anywhere" report. Refresh so a
        // finding a newer scan already re-anchored picks up its current tag
        // on the next click, instead of retrying the same dead one.
        const data = result.ok
          ? (result.data as { selected?: boolean; via?: string; moved?: boolean } | undefined)
          : undefined;
        if (data?.selected !== true) {
          // refresh() → load() sets error back to null synchronously as its
          // own first step (before its network await) — call it BEFORE
          // setting this message, not after, or load()'s reset silently
          // wins and the operator sees no feedback at all.
          refresh();
          setError("Couldn't find this item's location in the document — refreshing the list. Try again, or re-run the check.");
          return;
        }
        // Found by text, but the text no longer matches the fingerprint
        // captured when the finding was made. The jump itself is honest (it
        // is the right words in the right place), so it stands — but Apply
        // will refuse against a moved location, and saying so here is what
        // separates "this navigated and the fix is ready" from "this
        // navigated and the fix will be refused".
        if (data.via === "text" && data.moved === true) {
          setError(
            "Jumped to this text, but it has changed since the check ran — Apply will report it as out of date. Re-run the check to refresh it.",
          );
        }
      } catch (e) {
        setError("Couldn't find this item's location in the document.");
        logConnectorFailure("word-doc-review", e, "jump-to-finding");
      }
    },
    [refresh],
  );

  return { findings, loading, error, actingId, refresh, clearFindings, applyFinding, skipFinding, jumpToFinding };
}
